import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { queue } from 'async-es';
import { isEmpty } from 'lodash';
import canvasSize from 'canvas-size';
import progressSvg from './progress.svg';
import Hammer from 'hammerjs';
import ResizeObserver from 'resize-observer-polyfill';
import {
    PlanViewMouseMoveEvent,
    PlanViewPaintEvent,
    PlanViewResizeEvent,
    PlanViewClickEvent,
    PlanViewDocumentRenderedEvent,

    PLAN_VIEW_REPAINT_EVENT_TYPE,
    PLAN_VIEW_ZOOM_EXTENTS_EVENT_TYPE,
    PLAN_VIEW_REGEN_EVENT_TYPE
} from './PlanViewAdvanced2.PlanViewEvents';

// import { usePlanViewAdvanced2LayerSelectedContext } from './PlanViewAdvanced2.LayerSelectContext';

const pdfjsLib = require('pdfjs-dist/build/pdf');

const maxCanvasSize = {
    area: 10240 * 10240,
    width: 16384,
    height: 16384,
    magicFactor: 0.982
};

canvasSize.maxArea({
    onSuccess: function(width, height, benchmark) {
        maxCanvasSize.area = width * height;
    }
});
canvasSize.maxWidth({
    onSuccess: function(width, height, benchmark) {
        maxCanvasSize.width = width;
    }
});
canvasSize.maxHeight({
    onSuccess: function(width, height, benchmark) {
        maxCanvasSize.height = height;
    }
});


// 96 dpi divided by 72 typographic points per inch
const pdfScaleAdjust = 96.0/72.0;
const viewPointDefaultDrawProps = {
    background:'#FFFFFF',
    //fill:'#00AA00',
    fill:'#5577FF',
    stroke:'#3040DD'
};


const pdfRenderQueueWorkerFn = (task, callback) => {

    task.renderQueue.currentRenderTask = task.renderTask;
    task.renderTask.onContinue = continueFn => {
        continueFn();
    };

    (task.onProgressStart() && task.onProgressStart());

    task.renderTask.promise
        .then(() => {
            task.renderQueue.currentRenderTask = null;
            callback();
            (task.onProgressEnd() && task.onProgressEnd());
            (task.onPageRendered && task.onPageRendered());
        })
        .catch(err => {
            task.renderQueue.currentRenderTask = null;
            if (err instanceof pdfjsLib.RenderingCancelledException) {
                callback();
            }
            else {
                callback(err);
                console.error('render error', err)
            }
            (task.onRenderCancelled && task.onRenderCancelled());
        })
};


const pdfRenderQueue = queue(pdfRenderQueueWorkerFn, 1);


function pdfRenderQueueDrain() {
}


pdfRenderQueue.drain(pdfRenderQueueDrain.bind(pdfRenderQueue));



function PlanViewAdvanced2 (props, ref) {

    const {
        pdfPage,
        pdfPageViewport,
        pdfOptionalContentConfig,
        pdfVisibleGroups,
        children,
        backgroundColor,
        shadowColor,
        zIndexBase
    } = props;

//    const { selectedLayerIds } = usePlanViewAdvanced2LayerSelectedContext();

    const pdfViewDataRef = useRef({
        pdfPage:null,
        pdfPageViewport:null,
        pdfOptionalContentConfig:null,

        pdfRenderViewport:null,
        pdfViewTrMatrix:new DOMMatrix([1,0,0,1,0,0]),

        scale:pdfScaleAdjust,
        offsetX:0,
        offsetY:0,

        viewPointRadiusPx: window.isTablet ? 10 : 5,
    });


    const pdfRenderRef = useRef({
        renderQueue:pdfRenderQueue,
    });

    const containerRef = useRef(null);
    const canvasPlanDrawRef = useRef(null);
    const canvasPdfRenderRef = useRef(null);
    const canvasDrawSourceRef = useRef(null);

    const progressIndicatorRef = useRef(null);
    const resizeObserverRef = useRef(null);
    const interactionTargetRef = useRef(null);
    const gestureRecognizerRef = useRef(null);


    const drawViewpoint = (canvas, viewPoint, viewTrafo, options) => {

        let { background, fill, stroke, radius, drawAsSelected } = options || viewPointDefaultDrawProps;
        if (!background) background = viewPointDefaultDrawProps.background;
        if (!fill) fill = viewPointDefaultDrawProps.fill;
        if (!stroke) stroke = viewPointDefaultDrawProps.stroke;
        if (!radius) radius = pdfViewDataRef.current.viewPointRadiusPx;

        const svp = viewPoint.matrixTransform(viewTrafo);

        canvas.fillStyle = background;
        canvas.beginPath();
        canvas.arc(svp.x, svp.y, radius + 2, 0, Math.PI * 2);
        canvas.fill();

        canvas.fillStyle = fill;
        canvas.strokeStyle = stroke;
        canvas.lineWidth = 2;

        canvas.beginPath();
        canvas.arc(svp.x, svp.y, radius, 0, Math.PI * 2);
        canvas.fill();
        canvas.stroke();

        if (drawAsSelected) {
            canvas.lineWidth = 1.75;

            canvas.beginPath();
            canvas.arc(svp.x, svp.y, radius + canvas.lineWidth + 1.25, 0, Math.PI * 2);
            canvas.stroke();
        }
    };


    const drawViewpointLabel = (canvas, viewPoint, viewTrafo, options) => {

        const { background, fill, font } = options || {
            background:'rgba(255,255,255,0.5)',
            fill:'rgba(0,0,0,0.8)',
            font:`bold ${14}px sans-serif`
        };
        const radius = pdfViewDataRef.current.viewPointRadiusPx;


        const svp = viewPoint.matrixTransform(viewTrafo);

        canvas.font = font;

        const ts = canvas.measureText(viewPoint.name);
        const tx = svp.x - ts.width / 2;
        const ty = (svp.y - radius - 6);

        canvas.fillStyle = background;
        canvas.fillText(viewPoint.name, tx - 1, ty - 1);
        canvas.fillText(viewPoint.name, tx + 1, ty - 1);
        canvas.fillText(viewPoint.name, tx - 1, ty + 1);
        canvas.fillText(viewPoint.name, tx + 1, ty + 1);

        canvas.fillStyle = fill;
        canvas.fillText(viewPoint.name, tx, ty);
    };


    const calcZoomExtents = (pageViewport, viewSize) => {
        try {
            const sx = viewSize.width / pageViewport.width;
            const sy = viewSize.height / pageViewport.height;

            const sc = Math.min(sx, sy) * 0.97;
            const offX = (viewSize.width - pageViewport.width * sc) / 2.0;
            const offY = (viewSize.height - pageViewport.height * sc) / 2.0;

            return {
                scale:sc,
                offsetX:offX,
                offsetY:offY,
            };
        }
        catch {
            return {
                scale:pdfScaleAdjust,
                offsetX:0,
                offsetY:0,
            };
        }
    };


    const calcViewTrMatrix = () => {
        const t1_ViewScale = new DOMMatrix([
            pdfViewDataRef.current.scale,0,
            0,pdfViewDataRef.current.scale,
            0,0]);

        const t2_ViewMirror = new DOMMatrix([
            1,0,
            0,-1,
            0,pdfViewDataRef.current.pageViewport.height * pdfViewDataRef.current.scale]);

        const t3_ViewOffset = new DOMMatrix([
            1,0,
            0,1,
            pdfViewDataRef.current.offsetX,pdfViewDataRef.current.offsetY]);

        pdfViewDataRef.current.pdfViewTrMatrix = t3_ViewOffset.multiply(t2_ViewMirror).multiply(t1_ViewScale);
    };


    const onZoomExtents = () => {
        if (!pdfViewDataRef.current || !pdfViewDataRef.current.page)
            return;

        const pageViewport = pdfViewDataRef.current.page.getViewport({ scale:1, offsetX:0, offsetY:0 });
        const ext = calcZoomExtents(pageViewport, {
            width:canvasPlanDrawRef.current.width,
            height:canvasPlanDrawRef.current.height
        });
        const renderViewport = pdfViewDataRef.current.page.getViewport({
            scale:ext.scale,
            offsetX:0,
            offsetY:0,
        });

        pdfViewDataRef.current.scale = ext.scale;
        pdfViewDataRef.current.paintScale = ext.scale;
        pdfViewDataRef.current.minScale = ext.scale * 0.5;
        pdfViewDataRef.current.offsetX = ext.offsetX;
        pdfViewDataRef.current.offsetY = ext.offsetY;
        pdfViewDataRef.current.pdfRenderViewport = renderViewport;

        calcViewTrMatrix();

        onPaint();
        onRenderPage();
    }


    const onPaint = (options = { full:true }) => {
        if (!pdfViewDataRef.current)
            return;
        if (!pdfViewDataRef.current.pageViewport)
            return;

        const { full, plan, viewpoints } = options;
        const shadowSize = 5;

        if (full || plan) {
            const ctx = canvasPlanDrawRef.current.getContext("2d");

            ctx.setTransform(1, 0, 0, 1, 0, 0);
            //ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

            ctx.fillStyle = backgroundColor || '#666666';
            ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);

            ctx.fillStyle = shadowColor || '#444444';
            ctx.fillRect(pdfViewDataRef.current.offsetX + shadowSize,
                pdfViewDataRef.current.offsetY + shadowSize,
                canvasDrawSourceRef.current.width * pdfViewDataRef.current.paintScale,
                canvasDrawSourceRef.current.height * pdfViewDataRef.current.paintScale);

            ctx.drawImage(canvasDrawSourceRef.current,
                pdfViewDataRef.current.offsetX,
                pdfViewDataRef.current.offsetY,
                canvasDrawSourceRef.current.width * pdfViewDataRef.current.paintScale,
                canvasDrawSourceRef.current.height * pdfViewDataRef.current.paintScale
            );

            ctx.strokeStyle = '#1A1A1A';
            ctx.beginPath();
            ctx.rect(pdfViewDataRef.current.offsetX,
                pdfViewDataRef.current.offsetY,
                canvasDrawSourceRef.current.width * pdfViewDataRef.current.paintScale,
                canvasDrawSourceRef.current.height * pdfViewDataRef.current.paintScale
            );
            ctx.stroke();

            ctx.fillStyle = 'rgba(0,0,0,0)';
            ctx.strokeStyle = '#000000';
        }

        calcViewTrMatrix();

        try {
            if (ref.current instanceof EventTarget) {
                ref.current.dispatchEvent(new PlanViewPaintEvent({
                    viewTransform:pdfViewDataRef.current.pdfViewTrMatrix,
                    scale:pdfViewDataRef.current.scale,
                    viewPointRadiusPx:pdfViewDataRef.current.viewPointRadiusPx,
                    drawViewpoint,
                    drawViewpointLabel,
                }));
            }
        }
        catch (e) {
            console.log(e)
        }
    };


    const onRepaint = (options = { full:false }) => {
        onPaint(options);
    }


    const onRenderPage = () => {

        if (!pdfViewDataRef.current.page)
            return;

        (pdfRenderRef.current.renderQueue.currentRenderTask &&
         pdfRenderRef.current.renderQueue.currentRenderTask.cancel());

        new Promise((resolve, reject) => {

            canvasPdfRenderRef.current.width =
            canvasPdfRenderRef.current.height = 0;

            canvasPdfRenderRef.current.width =
                pdfViewDataRef.current.pdfRenderViewport.width +
                pdfViewDataRef.current.pdfRenderViewport.offsetX;
            canvasPdfRenderRef.current.height =
                pdfViewDataRef.current.pdfRenderViewport.height +
                pdfViewDataRef.current.pdfRenderViewport.offsetY;

            const clearRenderCanvas = () => {
                const ctx = canvasPdfRenderRef.current.getContext("2d");

                ctx.setTransform(1,0,0,1,0,0);
                //ctx.clearRect(0,0,ctx.canvas.width,ctx.canvas.height);
                //ctx.fillStyle = 'red';
                //ctx.fillRect(0,0,ctx.canvas.width,ctx.canvas.height);
            };

            clearRenderCanvas();

            let renderTask;
            try {
                pdfRenderRef.current.renderQueue.currentRenderTask =
                    renderTask = pdfViewDataRef.current.page.render({
                        annotationStorage:undefined,
                        background: 'ghostwhite',
                        canvasContext:canvasPdfRenderRef.current.getContext("2d"),
                        viewport:pdfViewDataRef.current.pdfRenderViewport,
                        enableWebGL:true,
                        optionalContentConfigPromise: new Promise((resolve, reject) => {
                            //pdfViewDataRef.current.optionalContentConfig.setVisibility("10R", false);
                            resolve(pdfViewDataRef.current.optionalContentConfig);
                        }),
                    });
            }
            catch (e) {
                console.error('render err', e)
            }

            //progressIndicatorRef.current.style.display = 'flex';

            pdfRenderRef.current.renderQueue.push({
                renderTask,
                renderQueue:pdfRenderRef.current.renderQueue,
                onProgressStart:() => {
                    progressIndicatorRef.current.style.display = 'flex';
                },
                onProgressEnd:() => {
                    progressIndicatorRef.current.style.display = 'none';
                },
                onPageRendered:() => {
                    canvasDrawSourceRef.current.width =
                    canvasDrawSourceRef.current.height = 0;
                    canvasDrawSourceRef.current.width = canvasPdfRenderRef.current.width;
                    canvasDrawSourceRef.current.height = canvasPdfRenderRef.current.height;

                    const ctx = canvasDrawSourceRef.current.getContext("2d");

                    ctx.setTransform(1, 0, 0, 1, 0, 0);
                    ctx.clearRect(0,0,ctx.canvas.width, ctx.canvas.height);

                    ctx.drawImage(canvasPdfRenderRef.current,
                        0,
                        0,
                        canvasDrawSourceRef.current.width,
                        canvasDrawSourceRef.current.height
                    );

                    pdfViewDataRef.current.paintScale = 1;

                    clearRenderCanvas();

                    onPaint();

                    if (ref.current instanceof EventTarget) {
                        ref.current.dispatchEvent(new PlanViewDocumentRenderedEvent());
                    }

                },
                onRenderCancelled: async () => {
                    clearRenderCanvas();
                }
            });

            resolve();
        })
            .catch(err => {
                console.error('err', err)
            })
        ;
    };


    const onResize = (width, height) => {

        if (!canvasPlanDrawRef.current)
            return;
        if (width < 1 || height < 1)
            return;

        canvasPlanDrawRef.current.width =
            // viewpointDrawCanvasRef.current.width =
            //     frustumDrawCanvasRef.current.width =
            //         jigDrawCanvasRef.current.width =
                        width;

        canvasPlanDrawRef.current.height =
            // viewpointDrawCanvasRef.current.height =
            //     frustumDrawCanvasRef.current.height =
            //         jigDrawCanvasRef.current.height =
                        height;

        if (ref.current instanceof EventTarget) {
            ref.current.dispatchEvent(new PlanViewResizeEvent(
                width,
                height
            ));
        }

        if (pdfViewDataRef.current.initialZoomExtents) {
            pdfViewDataRef.current.initialZoomExtents = false;
            onZoomExtents();
        }
        else {
            onPaint();
        }
    };


    useEffect(() => {
        if (!pdfPage)
            return;
        if (!pdfPageViewport)
            return;
        if (!pdfOptionalContentConfig)
            return;

        const samePage =
            pdfViewDataRef.current.page &&
            pdfViewDataRef.current.page === pdfPage;

        pdfViewDataRef.current.page = pdfPage;
        pdfViewDataRef.current.pageViewport = pdfPageViewport;
        pdfViewDataRef.current.optionalContentConfig = pdfOptionalContentConfig;


        const order = pdfOptionalContentConfig.getOrder() || [];
        const currentState = order.filter(id => pdfOptionalContentConfig.getGroup(id).visible);
        const newState = isEmpty(pdfVisibleGroups) ? order : pdfVisibleGroups;

        const layerVisibilitySame =
            currentState.length === newState.length &&
            currentState.reduce((acc, cv) => acc && newState.includes(cv), true);

        if (!layerVisibilitySame)
            order.forEach(id => pdfOptionalContentConfig.setVisibility(id, newState.includes(id)));


        if (!samePage) {
            const pageViewport = pdfPage.getViewport({ scale:1, offsetX:0, offsetY:0 });

            const maxC = maxCanvasSize.area / 3;

            let maxScale = maxC / (pageViewport.width * pageViewport.height);

            if (pageViewport.width * maxScale > maxCanvasSize.width) {
                maxScale = maxCanvasSize.width / pageViewport.width;
            }
            if (pageViewport.height * maxScale > maxCanvasSize.height) {
                maxScale = maxCanvasSize.height / pageViewport.height;
            }
            maxScale *= maxCanvasSize.magicFactor;

            const ext = calcZoomExtents(pageViewport, {
                width:canvasPlanDrawRef.current.width,
                height:canvasPlanDrawRef.current.height
            });
            if (ext.scale > maxScale)
                ext.scale = maxScale;

            const renderViewport = pdfPage.getViewport({
                scale:ext.scale,
                offsetX:0,
                offsetY:0,
            });

            pdfViewDataRef.current.scale = ext.scale;
            pdfViewDataRef.current.minScale = ext.scale * 0.5;
            pdfViewDataRef.current.maxScale = maxScale;
            pdfViewDataRef.current.offsetX = ext.offsetX;
            pdfViewDataRef.current.offsetY = ext.offsetY;
            pdfViewDataRef.current.pdfRenderViewport = renderViewport;

            calcViewTrMatrix();
        }


        if (!samePage || !layerVisibilitySame) {
            onRenderPage();
        }
        else {
            onPaint();
        }
    }, [
        pdfPage,
        pdfPageViewport,
        pdfOptionalContentConfig,
        pdfVisibleGroups
    ]);


    const onWheel = async (evt) => {
        if (pdfViewDataRef.current.isPanning)
            return;
        if (!evt.ctrlKey && !evt.altKey && !evt.shiftKey && !evt.metaKey)
        {
            evt.preventDefault();

            let factor = (evt.deltaY < 0) ? 1.25 : 1/1.25;
            let newScale = pdfViewDataRef.current.scale * factor;
            newScale =
                Math.max(pdfViewDataRef.current.minScale,
                         Math.min(newScale, pdfViewDataRef.current.maxScale))
            ;

            if (Math.abs(pdfViewDataRef.current.scale - newScale) < 0.01)
                return;

            const renderViewport = pdfViewDataRef.current.page.getViewport({
                scale:newScale,
                offsetX:0,
                offsetY:0,
            });

            let zoomX = (evt.offsetX - pdfViewDataRef.current.offsetX); // pdfViewDataRef.current.scale;
            let zoomY = (evt.offsetY - pdfViewDataRef.current.offsetY); // pdfViewDataRef.current.scale;

            const dx = (zoomX /*- pdfViewDataRef.current.offsetX*/) * (factor - 1),
                  dy = (zoomY /*- pdfViewDataRef.current.offsetY*/) * (factor - 1);

            const localScaleX = renderViewport.width / canvasPdfRenderRef.current.width;
            const localScaleY = renderViewport.height / canvasPdfRenderRef.current.height;

            pdfViewDataRef.current.scale = newScale;
            pdfViewDataRef.current.offsetX -= dx;
            pdfViewDataRef.current.offsetY -= dy;
            pdfViewDataRef.current.pdfRenderViewport = renderViewport;
            pdfViewDataRef.current.paintScale *= (localScaleX+localScaleY)/2.0;

            if (renderViewport.width < canvasPlanDrawRef.current.width) {
                pdfViewDataRef.current.offsetX = (canvasPlanDrawRef.current.width - renderViewport.width) / 2;
            }
            if (renderViewport.height < canvasPlanDrawRef.current.height) {
                pdfViewDataRef.current.offsetY = (canvasPlanDrawRef.current.height - renderViewport.height) / 2;
            }

            calcViewTrMatrix();

            onPaint();

            onRenderPage();
        }
    };


    const onPanStart = hammerEvt => {
        if (!pdfViewDataRef.current.pdfRenderViewport)
            return;

        pdfViewDataRef.current.isPanning = true;
        pdfViewDataRef.current.panStartAt = { x: hammerEvt.srcEvent.pageX, y: hammerEvt.srcEvent.pageY };
        pdfViewDataRef.current.panStartOffset = {
            x: pdfViewDataRef.current.offsetX,
            y: pdfViewDataRef.current.offsetY
        };
    };


    const onPanEnd = hammerEvt => {
        if (!pdfViewDataRef.current.pdfRenderViewport)
            return;

        pdfViewDataRef.current.isPanning = false;
    };


    const onPanCancel = hammerEvt => {
        if (!pdfViewDataRef.current.pdfRenderViewport)
            return;

        pdfViewDataRef.current.isPanning = false;
    };


    const onPan = hammerEvt => {
        if (!pdfViewDataRef.current.pdfRenderViewport)
            return;

        pdfViewDataRef.current.offsetX =
            pdfViewDataRef.current.panStartOffset.x +
            hammerEvt.srcEvent.pageX - pdfViewDataRef.current.panStartAt.x;
        pdfViewDataRef.current.offsetY =
            pdfViewDataRef.current.panStartOffset.y +
            hammerEvt.srcEvent.pageY - pdfViewDataRef.current.panStartAt.y;
        calcViewTrMatrix();

        onPaint({
            plan:true,
            viewpoints:true,
        });

    };


    const onPointerMove = evt => {
        if (!pdfViewDataRef.current.pdfRenderViewport)
            return;
        if (ref.current instanceof EventTarget) {

            let pixelX = evt.offsetX - pdfViewDataRef.current.offsetX;
            let pixelY = pdfViewDataRef.current.pdfRenderViewport.height - (evt.offsetY - pdfViewDataRef.current.offsetY);

            let scaledX = pixelX / pdfViewDataRef.current.scale;
            let scaledY = pixelY / pdfViewDataRef.current.scale;

            let paperX = Math.floor(scaledX / 72.0 * 2.54 * 10.0) / 10.0;
            let paperY = Math.floor(scaledY / 72.0 * 2.54 * 10.0) / 10.0;

            ref.current.dispatchEvent(new PlanViewMouseMoveEvent({
                pixelX, pixelY,
                scaledX, scaledY,
                paperX, paperY,
                altKey: evt.altKey,
                button: evt.button,
                buttons: evt.buttons,
                clientX: evt.clientX,
                clientY: evt.clientY,
                ctrlKey: evt.ctrlKey,
                metaKey: evt.metaKey,
                movementX: evt.movementX,
                movementY: evt.movementY,
                offsetX: evt.offsetX,
                offsetY: evt.offsetY,
                pageX: evt.pageX,
                pageY: evt.pageY,
                screenX: evt.screenX,
                screenY: evt.screenY,
                shiftKey: evt.shiftKey,
                which: evt.which,
                originalEvent:evt,

                viewPointRadiusPx:pdfViewDataRef.current.viewPointRadiusPx,
                scale:pdfViewDataRef.current.scale,
            }));
        }
    };


    const onClick = evt => {
        if (ref.current instanceof EventTarget) {

            let pixelX = evt.offsetX - pdfViewDataRef.current.offsetX;
            let pixelY = pdfViewDataRef.current.pdfRenderViewport.height - (evt.offsetY - pdfViewDataRef.current.offsetY);

            let scaledX = pixelX / pdfViewDataRef.current.scale;
            let scaledY = pixelY / pdfViewDataRef.current.scale;

            let paperX = Math.floor(scaledX / 72.0 * 2.54 * 10.0) / 10.0;
            let paperY = Math.floor(scaledY / 72.0 * 2.54 * 10.0) / 10.0;

            ref.current.dispatchEvent(new PlanViewClickEvent({
                pixelX, pixelY,
                scaledX, scaledY,
                paperX, paperY,
                altKey: evt.altKey,
                button: evt.button,
                buttons: evt.buttons,
                clientX: evt.clientX,
                clientY: evt.clientY,
                ctrlKey: evt.ctrlKey,
                metaKey: evt.metaKey,
                movementX: evt.movementX,
                movementY: evt.movementY,
                offsetX: evt.offsetX,
                offsetY: evt.offsetY,
                pageX: evt.pageX,
                pageY: evt.pageY,
                screenX: evt.screenX,
                screenY: evt.screenY,
                shiftKey: evt.shiftKey,
                which: evt.which,
                originalEvent:evt,

                viewPointRadiusPx:pdfViewDataRef.current.viewPointRadiusPx,
                scale:pdfViewDataRef.current.scale,
            }));
        }
    }


    useEffect(() => {
        resizeObserverRef.current = new ResizeObserver(entries => {
            if (entries?.length > 0)
            {
                const w = Math.ceil(entries[0].contentRect.width);
                const h = Math.ceil(entries[0].contentRect.height);

                onResize(w, h);
            }
        });

        resizeObserverRef.current.observe(containerRef.current);

        if (false) {
            const debugEvent = evt => {
                console.log(evt.type, evt);
            }

            interactionTargetRef.current.addEventListener("mouseenter", debugEvent);
            interactionTargetRef.current.addEventListener("mouseover", debugEvent);
            //interactionTargetRef.current.addEventListener("mousemove", debugEvent);
            interactionTargetRef.current.addEventListener("mouseout", debugEvent);
            interactionTargetRef.current.addEventListener("mouseleave", debugEvent);
            interactionTargetRef.current.addEventListener("mousedown", debugEvent);
            interactionTargetRef.current.addEventListener("mouseup", debugEvent);

            interactionTargetRef.current.addEventListener("wheel", debugEvent);

            interactionTargetRef.current.addEventListener("touchstart", debugEvent);
            interactionTargetRef.current.addEventListener("touchend", debugEvent);
            interactionTargetRef.current.addEventListener("touchmove", debugEvent);
            interactionTargetRef.current.addEventListener("touchcancel", debugEvent);

            interactionTargetRef.current.addEventListener("pointerover", debugEvent);
            interactionTargetRef.current.addEventListener("pointerenter", debugEvent);
            interactionTargetRef.current.addEventListener("pointerdown", debugEvent);
            //interactionTargetRef.current.addEventListener("pointermove", debugEvent);
            interactionTargetRef.current.addEventListener("pointerup", debugEvent);
            interactionTargetRef.current.addEventListener("pointerout", debugEvent);
            interactionTargetRef.current.addEventListener("pointerleave", debugEvent);
        }


        interactionTargetRef.current.addEventListener("wheel", onWheel);
        interactionTargetRef.current.addEventListener("pointermove", onPointerMove);
        interactionTargetRef.current.addEventListener("click", onClick);

        ref.current.addEventListener(PLAN_VIEW_REPAINT_EVENT_TYPE, onRepaint);
        ref.current.addEventListener(PLAN_VIEW_ZOOM_EXTENTS_EVENT_TYPE, onZoomExtents);
        ref.current.addEventListener(PLAN_VIEW_REGEN_EVENT_TYPE, onRenderPage);


        gestureRecognizerRef.current = new Hammer(interactionTargetRef.current, {
        });
        gestureRecognizerRef.current.get('pan').set({
            threshold: 10,
            direction: Hammer.DIRECTION_ALL
        });
        gestureRecognizerRef.current.on("panstart", onPanStart);
        gestureRecognizerRef.current.on("panend", onPanEnd);
        gestureRecognizerRef.current.on("pancancel", onPanCancel);
        gestureRecognizerRef.current.on("pan", onPan);

        return () => {
            resizeObserverRef.current?.disconnect();

            interactionTargetRef.current?.removeEventListener("wheel", onWheel);
            interactionTargetRef.current?.removeEventListener("pointermove", onPointerMove);
            interactionTargetRef.current?.removeEventListener("click", onClick);

            ref.current?.removeEventListener(PLAN_VIEW_REPAINT_EVENT_TYPE, onRepaint);
            ref.current?.removeEventListener(PLAN_VIEW_ZOOM_EXTENTS_EVENT_TYPE, onZoomExtents);
            ref.current?.removeEventListener(PLAN_VIEW_REGEN_EVENT_TYPE, onRenderPage);

            gestureRecognizerRef.current?.stop(true);
            gestureRecognizerRef.current?.destroy();
            gestureRecognizerRef.current = null;
        };
    }, []);


    return (
        <section ref={ref} style={{
            width:'100%',
            height:'100%',

        }}>
            <div ref={containerRef} style={{
                width:'100%',
                height:'100%',
                overflow: 'hidden',
                position:'relative',
                zIndex:zIndexBase
            }}>
                <canvas ref={canvasPdfRenderRef} id={'canvasTmp0'} hidden={true} width={1} height={1} />
                <canvas ref={canvasDrawSourceRef} id={'canvasTmp1'} hidden={true} width={1} height={1} />
                <canvas
                    ref={canvasPlanDrawRef}
                    id={'planDrawCanvas'}
                    width={128}
                    height={128}
                    style={{
                        position:'absolute',
                        left:0,
                        top:0,
                        pointerEvents:'none',
                        zIndex:zIndexBase+1
                    }}
                />
                <div ref={interactionTargetRef} style={{
                    position:'absolute',
                    left:0, top:0,right:0,bottom:0,
                    zIndex:zIndexBase+2
                }}/>
                {children}
                <div ref={progressIndicatorRef}
                     style={{
                         position:'absolute',
                         left:0,
                         top:0,
                         right:0,
                         bottom:0,
                         zIndex:zIndexBase+100,
                         display:'none',
                         alignItems:'center',
                         justifyContent:'center',
                         pointerEvents:'none'
                     }}
                >
                    <img src={progressSvg}
                         width={48}
                         height={48}/>
                </div>
            </div>
        </section>
    );
}


export default React.forwardRef(PlanViewAdvanced2);


PlanViewAdvanced2.propTypes = {
    pdfPage:PropTypes.object.isRequired,
    pdfPageViewport:PropTypes.object.isRequired,
    pdfOptionalContentConfig:PropTypes.object.isRequired,
    pdfVisibleGroups:PropTypes.array.isRequired,
    backgroundColor:PropTypes.string,
    shadowColor:PropTypes.string,
    zIndexBase:PropTypes.number,
}


PlanViewAdvanced2.defaultProps = {
    backgroundColor:'#666666',
    shadowColor:'#444444',
    zIndexBase:100,
}